/* eslint-disable complexity */
import { createReducer } from "reduxsauce";
import Types from "./types";
import { FORM_CONFIGS, FLOW_CONFIG } from "../../shared/pre-approval-on-fly-form/config";

export const INITIAL_STATE = {
    isModalOpen: false,
    entryPoint: null,

    // Direct from GET /quote response
    answers: null,
    currentStep: null,       // Step KEY string (e.g. "PAGE_1_FORM")
    initialStep: null,
    stepsList: [],
    resultPageKey: null,
    bestInterestRate: null,
    worstInterestRate: null,
    maxLoanAmount: null,
    quotes: null,
    pollStatus: null,
    errorResponse: null,
    flowType: null,

    // Form metadata from API (dynamic config)
    formConfigs: null,

    // UI state (client-only)
    isLoading: false,
    isSubmitting: false,
    isPolling: false,
    error: null,
    isGuestLoginInProgress: false,
    isCarFirstFlow: false,
    isEditFlow: false,
    improveQuotesFlow: false,
    refetchOnClose: false,
    reloadOnClose: false,
    redirectOnResult: false,
    modalConfig: {
        ctaText: "View all cars",
        ctaSecondaryText: "Proceed",
        secondaryRedirectUrl: "/cars24-finance"
    }
};

// Handlers
const openModal = (state, { entryPoint }) => ({
    ...state,
    isModalOpen: true,
    entryPoint,
    redirectOnResult: false
});

const closeModal = (state) => ({
    ...state,
    isModalOpen: false,
    refetchOnClose: false,
    reloadOnClose: false,
    redirectOnResult: false,
    isEditFlow: false,
    improveQuotesFlow: false
});

const setCurrentStep = (state, { step }) => ({
    ...state,
    currentStep: step
});

const setFormData = (state, { answers }) => ({
    ...state,
    answers
});

const updateFieldValue = (state, { fieldId, value }) => ({
    ...state,
    answers: {
        ...state.answers,
        [fieldId]: value
    }
});

const setLoading = (state, { isLoading }) => ({
    ...state,
    isLoading
});

const setError = (state, { error }) => ({
    ...state,
    error
});

const resetState = () => INITIAL_STATE;

const setModalConfig = (state, { config }) => ({
    ...state,
    modalConfig: {
        ...state.modalConfig,
        ...config
    }
});

const setEditFlow = (state, { isEditFlow }) => ({
    ...state,
    isEditFlow,
    ...(isEditFlow && { improveQuotesFlow: false })
});

const setInitialFormData = (state, { answers }) => ({
    ...state,
    answers: {
        ...state.answers,
        ...answers
    }
});

// Set form config from local FORM_CONFIGS (fallback when API is not available)
const setFormConfigFromLocal = (state) => ({
    ...state,
    stepsList: Object.keys(FORM_CONFIGS),
    currentStep: FLOW_CONFIG.defaultStep,
    isLoading: false
});

// Fetch config handlers (GET /api/v2/preapproval/quote)
const fetchConfigRequest = (state) => ({
    ...state,
    isLoading: true,
    error: null
});

// eslint-disable-next-line complexity
const fetchConfigSuccess = (state, { data }) => ({
    ...state,
    isLoading: false,
    answers: data.answers || null,
    currentStep: data.currentStep || FLOW_CONFIG.defaultStep,
    initialStep: data.initialStep || null,
    stepsList: data.stepsList || [],
    resultPageKey: data.resultPageKey || state.resultPageKey,
    bestInterestRate: data.bestInterestRate || null,
    worstInterestRate: data.worstInterestRate || null,
    quotes: data.quotes || null,
    pollStatus: data.pollStatus || null,
    errorResponse: data.errorResponse || null,
    maxLoanAmount: data.maxLoanAmount || null,
    flowType: data.flowType || state.flowType
});

const fetchConfigFailure = (state, { error }) => ({
    ...state,
    isLoading: false,
    error
});

// Submit step handlers (PUT /quote or POST /submit)
const submitStepRequest = (state) => ({
    ...state,
    isSubmitting: true,
    error: null
});

const submitStepSuccess = (state, { data }) => ({
    ...state,
    isSubmitting: false,
    answers: data.answers || state.answers,
    currentStep: data.currentStep || state.currentStep,
    stepsList: data.stepsList || state.stepsList,
    resultPageKey: data.resultPageKey || state.resultPageKey,
    bestInterestRate: data.bestInterestRate || state.bestInterestRate,
    worstInterestRate: data.worstInterestRate || state.worstInterestRate,
    maxLoanAmount: data.maxLoanAmount || state.maxLoanAmount,
    quotes: data.quotes || state.quotes,
    errorResponse: data.errorResponse || state.errorResponse,
    flowType: data.flowType || state.flowType,
    redirectOnResult: !!data.resultPageKey || state.redirectOnResult
});

const submitStepFailure = (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error
});

// Poll handlers (GET /api/v2/preapproval/poll)
const pollStart = (state) => ({
    ...state,
    isPolling: true
});

const pollSuccess = (state, { data }) => ({
    ...state,
    isPolling: false,
    currentStep: data.currentStep || state.currentStep,
    stepsList: data.stepsList || state.stepsList,
    pollStatus: data.pollStatus || null,
    resultPageKey: data.resultPageKey || state.resultPageKey,
    bestInterestRate: data.bestInterestRate || state.bestInterestRate,
    worstInterestRate: data.worstInterestRate || state.worstInterestRate,
    maxLoanAmount: data.maxLoanAmount || state.maxLoanAmount,
    quotes: data.quotes || state.quotes,
    errorResponse: data.errorResponse || state.errorResponse,
    flowType: data.flowType || state.flowType,
    redirectOnResult: !!data.resultPageKey || state.redirectOnResult
});

const pollFailure = (state, { error }) => ({
    ...state,
    isPolling: false,
    error
});

// Guest login handler
const setGuestLoginInProgress = (state, { isGuestLoginInProgress }) => ({
    ...state,
    isGuestLoginInProgress
});

// Fetch form metadata handlers (GET /config/metadata?scenario=PreApproval_V2_Form)
const fetchFormMetadataRequest = (state) => ({
    ...state
});

const fetchFormMetadataSuccess = (state, { data }) => ({
    ...state,
    formConfigs: data
});

const clearResultPageKey = (state) => ({
    ...state,
    resultPageKey: null
});

const setImproveQuotesFlow = (state, { improveQuotesFlow }) => ({
    ...state,
    improveQuotesFlow,
    ...(improveQuotesFlow && { isEditFlow: false })
});

const fetchFormMetadataFailure = (state) => ({
    ...state,
    formConfigs: null
});

// Create reducer
export default createReducer(INITIAL_STATE, {
    [Types.OPEN_MODAL]: openModal,
    [Types.CLOSE_MODAL]: closeModal,
    [Types.SET_CURRENT_STEP]: setCurrentStep,
    [Types.SET_FORM_DATA]: setFormData,
    [Types.UPDATE_FIELD_VALUE]: updateFieldValue,
    [Types.SET_LOADING]: setLoading,
    [Types.SET_ERROR]: setError,
    [Types.RESET_STATE]: resetState,
    [Types.SET_MODAL_CONFIG]: setModalConfig,
    [Types.SET_EDIT_FLOW]: setEditFlow,
    [Types.SET_INITIAL_FORM_DATA]: setInitialFormData,
    [Types.SET_FORM_CONFIG_FROM_LOCAL]: setFormConfigFromLocal,
    [Types.FETCH_CONFIG_REQUEST]: fetchConfigRequest,
    [Types.FETCH_CONFIG_SUCCESS]: fetchConfigSuccess,
    [Types.FETCH_CONFIG_FAILURE]: fetchConfigFailure,
    [Types.SUBMIT_STEP_REQUEST]: submitStepRequest,
    [Types.SUBMIT_STEP_SUCCESS]: submitStepSuccess,
    [Types.SUBMIT_STEP_FAILURE]: submitStepFailure,
    [Types.POLL_START]: pollStart,
    [Types.POLL_SUCCESS]: pollSuccess,
    [Types.POLL_FAILURE]: pollFailure,
    [Types.GUEST_LOGIN_IN_PROGRESS]: setGuestLoginInProgress,
    [Types.FETCH_FORM_METADATA_REQUEST]: fetchFormMetadataRequest,
    [Types.FETCH_FORM_METADATA_SUCCESS]: fetchFormMetadataSuccess,
    [Types.FETCH_FORM_METADATA_FAILURE]: fetchFormMetadataFailure,
    [Types.CLEAR_RESULT_PAGE_KEY]: clearResultPageKey,
    [Types.SET_CAR_FIRST_FLOW]: (state, { isCarFirstFlow }) => ({ ...state, isCarFirstFlow }),
    [Types.SET_IMPROVE_QUOTES_FLOW]: setImproveQuotesFlow,
    [Types.SET_REFETCH_ON_CLOSE]: (state, { refetchOnClose }) => ({ ...state, refetchOnClose }),
    [Types.SET_RELOAD_ON_CLOSE]: (state, { reloadOnClose }) => ({ ...state, reloadOnClose }),
    [Types.SET_REDIRECT_ON_RESULT]: (state, { redirectOnResult }) => ({ ...state, redirectOnResult })
});
